import React, { useContext, useEffect, useState } from 'react';
import { checkoutAttendance, fetchTodayAttendance } from '../../api/Attendance';
import { AuthContext } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import SelfieCapture from './SelfieCapture';
import '../../css/modal.css';
import * as FiIcons from 'react-icons/fi';
import * as MdIcons from 'react-icons/md';

const MdCheckCircle = MdIcons.MdCheckCircle as unknown as React.FC;
const FiCamera = FiIcons.FiCamera as unknown as React.FC;

interface QuickCheckOutModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const QuickCheckOutModal: React.FC<QuickCheckOutModalProps> = ({ onClose, onSuccess }) => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [selfieFile, setSelfieFile] = useState<File | null>(null);
  const [selfiePreview, setSelfiePreview] = useState<string | null>(null);
  const [showSelfieCapture, setShowSelfieCapture] = useState(false);
  const [checkingExisting, setCheckingExisting] = useState(true);
  const [existingAttendance, setExistingAttendance] = useState<any>(null);
  const [alreadyCheckedOut, setAlreadyCheckedOut] = useState(false);
  const [missingAttendance, setMissingAttendance] = useState(false);

  const currentUserId = user?.id ? (typeof user.id === 'string' ? parseInt(user.id) : user.id) : 0;
  const userEmail = user?.email || '';
  const now = new Date();
  const currentDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate()
  ).padStart(2, '0')}`;
  const currentTime = now.toTimeString().slice(0, 5); // HH:MM

  useEffect(() => {
    const check = async () => {
      if (!currentUserId) return;
      try {
        setCheckingExisting(true);
        const todayAttendances = await fetchTodayAttendance(currentUserId);
        const existing =
          Array.isArray(todayAttendances) && todayAttendances.length > 0
            ? todayAttendances[0]
            : null;
        if (!existing) {
          setMissingAttendance(true);
          return;
        }
        setExistingAttendance(existing);
        if (existing.checkOutTime) {
          setAlreadyCheckedOut(true);
        }
      } catch (error) {
        setMissingAttendance(true);
      } finally {
        setCheckingExisting(false);
      }
    };
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelfieCapture = (file: File) => {
    setSelfieFile(file);
    const previewUrl = URL.createObjectURL(file);
    setSelfiePreview(previewUrl);
    setShowSelfieCapture(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUserId) {
      toast.error('User not found. Please contact administrator.');
      return;
    }

    if (!existingAttendance?.id) {
      toast.error('Attendance not found for today. Please check in first.');
      return;
    }

    if (alreadyCheckedOut) {
      toast.info('You are already checked out for today.');
      return;
    }

    if (!selfieFile) {
      toast.error('Please capture a checkout selfie');
      return;
    }

    try {
      setLoading(true);
      await checkoutAttendance(existingAttendance.id, selfieFile, currentTime);
      toast.success('Checked out successfully!');
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to check out');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: '500px', maxWidth: '90vw' }}>
          <button className="close-btn" onClick={onClose}>&times;</button>
          <h2>Check Out</h2>

          {checkingExisting ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <div>Checking today’s attendance...</div>
            </div>
          ) : missingAttendance ? (
            <div style={{ padding: '20px' }}>
              <div style={{ padding: '16px', backgroundColor: '#fff3e0', borderRadius: '8px', border: '1px solid #ffcc80' }}>
                Attendance not found for today. Please mark attendance (check-in) first.
              </div>
              <div className="modal-actions" style={{ marginTop: '16px' }}>
                <button type="button" onClick={onClose} className="primary">Close</button>
              </div>
            </div>
          ) : alreadyCheckedOut ? (
            <div style={{ padding: '20px' }}>
              <div style={{
                padding: '20px',
                backgroundColor: '#e8f5e9',
                borderRadius: '8px',
                border: '2px solid #4caf50',
                textAlign: 'center',
                marginBottom: '20px'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '10px' }}>
                  <span style={{ color: '#4caf50' }}><MdCheckCircle /></span>
                </div>
                <h3 style={{ margin: '0 0 10px 0', color: '#2e7d32' }}>Already Checked Out</h3>
                {existingAttendance?.checkOutTime && (
                  <p style={{ margin: 0, color: '#666' }}>
                    Check-out time: {existingAttendance.checkOutTime}
                  </p>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" onClick={onClose} className="primary">Close</button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <div style={{ marginBottom: '10px' }}>
                  <strong>User:</strong> {userEmail || user?.email || '—'}
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <strong>Date:</strong> {new Date(currentDate).toLocaleDateString()}
                </div>
                <div>
                  <strong>Time:</strong> {currentTime}
                </div>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Checkout Selfie *
                </label>
                {selfiePreview ? (
                  <div style={{ marginBottom: '10px' }}>
                    <img
                      src={selfiePreview}
                      alt="Checkout selfie preview"
                      style={{
                        width: '100%',
                        maxWidth: '300px',
                        height: 'auto',
                        borderRadius: '8px',
                        border: '2px solid #ddd'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowSelfieCapture(true)}
                      style={{
                        marginTop: '8px',
                        padding: '8px 16px',
                        background: '#ff9800',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <FiCamera /> Retake Selfie
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowSelfieCapture(true)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#4caf50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <FiCamera /> Capture Selfie
                  </button>
                )}
              </div>

              <div className="modal-actions">
                <button type="submit" disabled={loading}>
                  {loading ? 'Saving…' : 'Check Out'}
                </button>
                <button type="button" onClick={onClose} className="secondary" disabled={loading}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {showSelfieCapture && (
        <SelfieCapture
          onCapture={handleSelfieCapture}
          onCancel={() => setShowSelfieCapture(false)}
          existingImage={selfiePreview || undefined}
        />
      )}
    </>
  );
};

export default QuickCheckOutModal;
